import { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Clock, AlertTriangle, CheckCircle2, ClipboardCheck, X } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Demanda, EntregaEscopo } from '@/lib/types';

interface Notification {
  id: string;
  type: 'sla' | 'aprovacao' | 'atribuida' | 'revisao-escopo' | 'info';
  title: string;
  subtitle?: string;
  actionUrl?: string;
  icon: typeof Bell;
  color: string;
}

const READ_KEY = 'apex_notif_read';
function loadReadLocal(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) ?? '[]')); } catch { return new Set(); }
}
function saveReadLocal(ids: Set<string>) {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...ids].slice(-200))); } catch { /* noop */ }
}

export function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(loadReadLocal);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Sync from backend on mount (merge with local cache)
  const notifReadQ = useQuery({
    queryKey: ['notif-read'],
    queryFn: () => api.notifRead.get().then(r => r.ids),
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (notifReadQ.data) {
      setReadIds(prev => {
        const merged = new Set([...prev, ...notifReadQ.data!]);
        saveReadLocal(merged);
        return merged;
      });
    }
  }, [notifReadQ.data]);

  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then(r => r.data), staleTime: 60_000 });
  const escopoQ = useQuery({ queryKey: ['escopo-all'], queryFn: () => api.escopoEntregas.list().then(r => r.data), staleTime: 60_000 });

  const notifications = useMemo(() => {
    const notifs: Notification[] = [];
    const demandas: Demanda[] = demandasQ.data ?? [];
    const escopo: EntregaEscopo[] = escopoQ.data ?? [];
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const in2days = new Date(now);
    in2days.setDate(in2days.getDate() + 2);
    const in2daysStr = in2days.toISOString().slice(0, 10);

    if (!user) return notifs;

    // SLA approaching (not done, prazo in next 2 days)
    for (const d of demandas) {
      const done = d.status === 'Publicada' || d.status === 'Arquivada' || d.status === 'Cancelada';
      if (done || !d.prazoSla) continue;
      const sla = d.prazoSla.slice(0, 10);

      if (sla < todayStr) {
        notifs.push({
          id: `sla-overdue-${d.id}`,
          type: 'sla',
          title: `"${d.titulo}" está atrasada`,
          subtitle: `SLA era ${new Date(d.prazoSla).toLocaleDateString('pt-BR')}`,
          actionUrl: `/producao?abrir=${d.id}`,
          icon: AlertTriangle,
          color: 'text-red-500',
        });
      } else if (sla <= in2daysStr) {
        notifs.push({
          id: `sla-soon-${d.id}`,
          type: 'sla',
          title: `"${d.titulo}" vence ${sla === todayStr ? 'hoje' : 'em breve'}`,
          subtitle: `SLA: ${new Date(d.prazoSla).toLocaleDateString('pt-BR')}`,
          actionUrl: `/producao?abrir=${d.id}`,
          icon: Clock,
          color: 'text-amber-500',
        });
      }
    }

    // Demandas assigned to me in Triagem (for executors)
    if (user.papel === 'Executor' || user.papel === 'Coordenação') {
      const minhas = demandas.filter(d => d.executorId === user.pessoaId && d.status === 'Triagem');
      for (const d of minhas) {
        notifs.push({
          id: `atribuida-${d.id}`,
          type: 'atribuida',
          title: `"${d.titulo}" atribuída a você`,
          subtitle: 'Aguardando início',
          actionUrl: `/producao?abrir=${d.id}`,
          icon: ClipboardCheck,
          color: 'text-blue-500',
        });
      }
    }

    // Demandas waiting approval (for coordination)
    if (user.papel === 'Coordenação') {
      const aguardando = demandas.filter(d => d.status === 'Aguardando aprovação');
      if (aguardando.length > 0) {
        notifs.push({
          id: `aprovacao-demandas-${aguardando.length}`,
          type: 'aprovacao',
          title: `${aguardando.length} ${aguardando.length === 1 ? 'demanda aguarda' : 'demandas aguardam'} aprovação`,
          actionUrl: '/producao',
          icon: CheckCircle2,
          color: 'text-pink-500',
        });
      }

      // Escopo with revision requested
      const revisao = escopo.filter(e => e.status === 'Revisão solicitada');
      if (revisao.length > 0) {
        notifs.push({
          id: `revisao-escopo-${revisao.length}`,
          type: 'revisao-escopo',
          title: `${revisao.length} ${revisao.length === 1 ? 'entrega do escopo precisa' : 'entregas do escopo precisam'} de ajuste`,
          subtitle: 'Cliente pediu revisão',
          actionUrl: '/comercial',
          icon: AlertTriangle,
          color: 'text-purple-500',
        });
      }
    }

    return notifs;
  }, [demandasQ.data, escopoQ.data, user]);

  const unread = notifications.filter(n => !readIds.has(n.id)).length;

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function persistRead(ids: Set<string>) {
    saveReadLocal(ids);
    api.notifRead.save([...ids]).catch(() => { /* silent */ });
  }

  function markAllRead() {
    const newRead = new Set(readIds);
    notifications.forEach(n => newRead.add(n.id));
    setReadIds(newRead);
    persistRead(newRead);
  }

  function handleClick(n: Notification) {
    const newRead = new Set(readIds);
    newRead.add(n.id);
    setReadIds(newRead);
    persistRead(newRead);
    setOpen(false);
    if (n.actionUrl) navigate(n.actionUrl);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); }}
        className={clsx(
          'w-8 h-8 flex items-center justify-center rounded-full relative',
          'text-muted hover:text-text hover:bg-panel-2 transition-colors',
        )}
        title="Notificações"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-panel border border-border rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <span className="text-xs font-bold text-text">Notificações</span>
            {unread > 0 && (
              <button type="button" onClick={markAllRead} className="text-[10px] text-blue-light hover:underline">
                Marcar tudo como lido
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted">
                Nenhuma notificação por agora
              </div>
            ) : (
              notifications.map(n => {
                const Icon = n.icon;
                const isRead = readIds.has(n.id);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleClick(n)}
                    className={clsx(
                      'w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-panel-2/50 transition-colors border-b border-border/50 last:border-0',
                      !isRead && 'bg-blue-50/30 dark:bg-blue-900/10',
                    )}
                  >
                    <Icon className={clsx('w-4 h-4 mt-0.5 shrink-0', n.color)} />
                    <div className="flex-1 min-w-0">
                      <div className={clsx('text-[12px] leading-snug', isRead ? 'text-text-2' : 'text-text font-semibold')}>
                        {n.title}
                      </div>
                      {n.subtitle && <div className="text-[10px] text-muted mt-0.5">{n.subtitle}</div>}
                    </div>
                    {!isRead && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
